import { Alert, AlertTitle, Button, Stack } from "@mui/material";
import React, { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PropertiesCard } from "shared-ts-ui-properties-card";
import { FormikErrors } from "formik";
import { useIdentityDao } from "../dao/useIdentityDao";
import { Property, PropertyMode } from "shared-ts-property";
import { PasswordValidator } from "../PasswordValidator";
import { ChangePasswordResult } from "../dao/ChangePasswordResult";
import { resolvePasswordErrorMessages } from "./resolvePasswordErrorMessages";
import { createFormHook, createFormHookContexts } from "@tanstack/react-form";
import { ProblemDetailsFetchError } from "shared-ts-utils-fetch";
import { FormStrategy } from "./FormStrategy";

const { fieldContext, formContext } = createFormHookContexts();

const { useAppForm } = createFormHook({
  fieldComponents: {
    Property,
  },
  formComponents: {
    Button,
  },
  fieldContext,
  formContext,
});

type ChangePasswordFormValues = {
  username: string;
  code: string;
  password: string;
  confirmPassword: string;
};

/**
 * Maps query parameter error codes from form submission redirects to ChangePasswordResult.
 */
const mapErrorToResult = (
  error: string | null
): ChangePasswordResult | undefined => {
  if (error === null) return undefined;
  switch (error) {
    case "invalid_token":
      return "invalid-token";
    case "password_mismatch":
    case "failed":
    default:
      return "failed";
  }
};

/**
 * Maps query parameter result codes from form submission redirects to ChangePasswordResult.
 */
const mapResultParam = (
  result: string | null
): ChangePasswordResult | undefined => {
  if (result === "succeeded") return "succeeded";
  return undefined;
};

export const ChangePasswordForm: FC = () => {
  const { t } = useTranslation("shared-ts-ui-identity");
  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [window.location.search]
  );

  // Parse query params for redirect results from form submission
  const resultParam = searchParams.get("result");
  const errorParam = searchParams.get("error");
  const errorDescriptionParam = searchParams.get("error_description");

  // Initialize state from query params if present (for form submission redirects)
  const initialResult =
    mapResultParam(resultParam) ?? mapErrorToResult(errorParam);
  const initialDescription = errorDescriptionParam ?? undefined;

  const [resetPasswordResult, setResetPasswordResult] = useState<
    ChangePasswordResult | undefined
  >(initialResult);
  const [resetPasswordResultDescription, setResetPasswordResultDescription] =
    useState<string | undefined>(initialDescription);

  const identityDao = useIdentityDao();
  // Hardcoded to "submit" for now to trigger browser password manager heuristics.
  // Can be changed to "api" for background API calls without page navigation.
  const formStrategy = "submit" as FormStrategy;

  const propertyMode: PropertyMode =
    resetPasswordResult === "invalid-token" ||
    resetPasswordResult === "succeeded"
      ? "edit"
      : "edit";

  const initialValues: ChangePasswordFormValues = {
    username: searchParams.get("email") ?? "",
    code: searchParams.get("code") ?? "",
    password: "",
    confirmPassword: "",
  };

  /**
   * Handles API-based form submission (when formStrategy is "api").
   */
  const handleApiSubmit = async (value: ChangePasswordFormValues) => {
    try {
      const resetPasswordResponse = await identityDao.changePassword({
        username: value.username,
        code: value.code,
        password: value.password,
        confirmPassword: value.confirmPassword,
      });
      setResetPasswordResult(resetPasswordResponse.result);
      setResetPasswordResultDescription(resetPasswordResponse.description);
    } catch (e) {
      setResetPasswordResult("failed");
      if (e instanceof ProblemDetailsFetchError) {
        const errorMessages = resolvePasswordErrorMessages(e, t);
        const errorMessage = errorMessages.join(",\n");
        setResetPasswordResultDescription(errorMessage);
      } else if (e instanceof Error) {
        setResetPasswordResultDescription(e.message);
      } else {
        setResetPasswordResultDescription("error");
      }
    }
  };

  const form = useAppForm({
    defaultValues: initialValues,
    onSubmit: async ({ value }) => {
      // Only used for "api" strategy
      if (formStrategy === "api") {
        await handleApiSubmit(value);
      }
      // For "submit" strategy, native form submission handles it
    },
  });

  const handleValidate = (
    values: ChangePasswordFormValues
  ): FormikErrors<ChangePasswordFormValues> => {
    const errors: FormikErrors<ChangePasswordFormValues> = {};

    if (values.username.isBlank()) {
      errors.username = t("Required");
    }

    const passwordValidationResult = new PasswordValidator({
      t: t,
      requiredLength: 6,
    }).validate(values);

    if (passwordValidationResult.password != null) {
      errors.password = passwordValidationResult.password;
    }
    if (passwordValidationResult.confirmPassword != null) {
      errors.confirmPassword = passwordValidationResult.confirmPassword;
    }
    return errors;
  };

  const handleTryAgainClick = () => {
    window.location.href = "/";
  };

  const handleGotoLoginClick = () => {
    window.location.href = "/";
  };

  /**
   * Form submission handler that conditionally prevents default based on strategy.
   */
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (formStrategy === "api") {
      e.preventDefault();
      form.handleSubmit();
    }
    // For "submit" strategy, allow native form submission to proceed
  };

  // Form attributes for native submission
  const formAction =
    formStrategy === "submit" ? "/identity/change-password" : undefined;
  const formMethod = formStrategy === "submit" ? "POST" : undefined;

  return (
    <form
      autoComplete={"on"}
      action={formAction}
      method={formMethod}
      onSubmit={handleFormSubmit}
    >
      <PropertiesCard
        header={{
          title: t("Set New Password"),
        }}
        sx={{
          justifyContent: "center",
        }}
      >
        {/* Hide form fields when password change succeeded with submit strategy */}
        {!(
          resetPasswordResult === "succeeded" && formStrategy === "submit"
        ) && (
          <>
            {/* Hidden field for code - required for form submission */}
            <form.AppField
              name={"code"}
              children={(field) => (
                <input
                  name={"code"}
                  type={"hidden"}
                  value={field.state.value}
                />
              )}
            />
            {/* Visible username field (read-only) */}
            <form.AppField
              name={"username"}
              children={(field) => (
                <Property
                  name={"username"}
                  label={t("Email")}
                  type={"string"}
                  value={field.state.value}
                  autoComplete={"username"}
                  mode={"edit"}
                  fullWidth
                  inputProps={{
                    id: "username",
                    type: "email",
                    inputMode: "email",
                    autoCapitalize: "none",
                    autoCorrect: "off",
                    spellCheck: false,
                  }}
                  onValueChange={(value) => {
                    /* Prohibit editing of username field*/
                  }}
                />
              )}
            />
            {/* Password field */}
            <form.AppField
              name={"password"}
              children={(field) => (
                <Property
                  autoFocus
                  name={"password"}
                  label={t("New Password")}
                  type={"password"}
                  value={field.state.value}
                  autoComplete={"new-password"}
                  mode={propertyMode}
                  fullWidth
                  inputProps={{
                    id: "new-password",
                    autoCapitalize: "none",
                    autoCorrect: "off",
                    spellCheck: false,
                  }}
                  onValueChange={(value) => field.setValue(value as string)}
                />
              )}
            />
            {/* Confirm password field */}
            <form.AppField
              name={"confirmPassword"}
              children={(field) => (
                <Property
                  name={"confirmPassword"}
                  label={t("Confirm New Password")}
                  type={"password"}
                  value={field.state.value}
                  autoComplete={"new-password"}
                  mode={"edit"}
                  fullWidth
                  inputProps={{
                    id: "confirm-new-password",
                    autoCapitalize: "none",
                    autoCorrect: "off",
                    spellCheck: false,
                  }}
                  onValueChange={(value) => field.setValue(value as string)}
                />
              )}
            />
          </>
        )}
        {(resetPasswordResult === undefined ||
          resetPasswordResult === "failed") && (
          <Stack
            direction={"row"}
            sx={{ width: "100%", justifyContent: "space-evenly" }}
          >
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
            >
              {([canSubmit, isSubmitting]) => (
                <Button
                  type="submit"
                  color={"secondary"}
                  variant={"contained"}
                  disabled={!canSubmit}
                  loading={isSubmitting}
                >
                  {t("Save")}
                </Button>
              )}
            </form.Subscribe>
          </Stack>
        )}
        {resetPasswordResult === "succeeded" && (
          <>
            <Alert severity={"info"}>{t("Password Changed")}</Alert>
            <Button onClick={handleGotoLoginClick}>{t("Log In")}</Button>
          </>
        )}
        {resetPasswordResult === "failed" && (
          <Alert severity={"info"}>
            <AlertTitle>{t("Failed to Change Password")}</AlertTitle>
            {resetPasswordResultDescription && resetPasswordResultDescription}
          </Alert>
        )}
        {resetPasswordResult === "invalid-token" && (
          <>
            <Alert severity={"info"}>
              {t("Change Password Token Expired")}
              {t("Please request '{{val}}' again", {
                val: t("Forgot Password"),
              })}
            </Alert>
            <Button onClick={handleTryAgainClick}>{t("Try again")}</Button>
          </>
        )}
      </PropertiesCard>
    </form>
  );
};
